import { useState, useRef, useEffect, forwardRef, useCallback } from "react";
import { Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { checkIfTouchDevice } from "@/lib/browser-information/checkIfTouchDevice.util";

interface PointsInputProps {
	id?: string;
	value: number | string;
	min?: number;
	max?: number;
	step?: number;
	disabled?: boolean;
	className?: string;
	title?: string;
	placeholder?: string;
	onChange: (value: number) => void;
	onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	tabIndex?: number;
}

/**
 * PointsInput - A tablet-friendly input component for entering points
 *
 * Features:
 * - Standard number input for desktop
 * - Large touch-friendly increment/decrement buttons for tablets
 * - Responsive design that adapts to screen size
 */
export const PointsInput = forwardRef<HTMLInputElement, PointsInputProps>(
	(
		{
			id,
			value,
			min = 0,
			max = 100,
			step = 0.5,
			disabled = false,
			className = "",
			title,
			placeholder,
			onChange,
			onClick,
			onKeyDown,
			tabIndex,
		},
		ref,
	) => {
		const [internalValue, setInternalValue] = useState<number>(
			typeof value === "string" ? Number.parseFloat(value) || 0 : value,
		);
		const inputRef = useRef<HTMLInputElement>(null);
		const [isTouchDevice, setisTouchDevice] = useState(false);

		useEffect(() => {
			const update = () => setisTouchDevice(checkIfTouchDevice());
			update();
			window.addEventListener("resize", update);
			return () => window.removeEventListener("resize", update);
		}, []);

		// Sync internal value with prop value
		useEffect(() => {
			const newValue =
				typeof value === "string" ? Number.parseFloat(value) || 0 : value;
			setInternalValue(newValue);
		}, [value]);

		const handleIncrement = useCallback(() => {
			if (disabled) return;
			const newValue = Math.min(internalValue + step, max);
			setInternalValue(newValue);
			onChange(newValue);
		}, [disabled, internalValue, step, max, onChange]);

		const handleDecrement = useCallback(() => {
			if (disabled) return;
			const newValue = Math.max(internalValue - step, min);
			setInternalValue(newValue);
			onChange(newValue);
		}, [disabled, internalValue, step, min, onChange]);

		const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue =
				e.target.value === "" ? 0 : Number.parseFloat(e.target.value);
			if (!Number.isNaN(newValue)) {
				setInternalValue(newValue);
				onChange(newValue);
			}
		};

		const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
			if (isTouchDevice) {
				// Prevent focusing / opening the software keyboard on iPad/tablets.
				e.preventDefault();
				e.stopPropagation();
				e.currentTarget.blur();
				return;
			}
			onClick?.(e);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			// Handle arrow keys for increment/decrement
			if (e.key === "ArrowUp" || e.key === "ArrowRight") {
				e.preventDefault();
				handleIncrement();
			} else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
				e.preventDefault();
				handleDecrement();
			}

			// Call the external onKeyDown handler if provided
			onKeyDown?.(e);
		};

		const handleBlur = () => {
			// Enforce min/max bounds on blur
			if (internalValue < min) {
				const correctedValue = min;
				setInternalValue(correctedValue);
				onChange(correctedValue);
			} else if (internalValue > max) {
				const correctedValue = max;
				setInternalValue(correctedValue);
				onChange(correctedValue);
			}
		};

		// Calculate dynamic width based on content length
		const getInputWidth = () => {
			const valueString = internalValue.toString();
			const minWidth = 30; // Minimum width in pixels
			const charWidth = 8; // Approximate character width
			return Math.max(minWidth, valueString.length * charWidth);
		};

		return (
			<div className="relative flex ">
				{/* Decrement Button (left side) */}
				<Button
					type="button"
					variant="outline"
					disabled={disabled || internalValue <= min}
					onClick={handleDecrement}
					aria-label="Verringern"
					tabIndex={-1}
					data-enabled={!(disabled || internalValue <= min)}
					className="h-9 w-7 p-0 rounded-l-md rounded-r-none border-input border-r-0 hover:bg-gray-50 flex items-center justify-center shadow-none"
				>
					<Minus size={10} strokeWidth={2} />
				</Button>

				<input
					ref={ref || inputRef}
					id={id}
					type="number"
					min={min}
					max={max}
					step={step}
					disabled={disabled}
					readOnly={isTouchDevice}
					placeholder={placeholder}
					value={internalValue}
					onChange={handleInputChange}
					onClick={handleInputClick}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					style={{
						width: `${getInputWidth()}px`,
						paddingLeft: "0px",
						paddingRight: "0px",
					}}
					className={`
						h-9 text-sm text-center border border-r-0 border-l-0 border-input 
						focus:outline-none p-0
						[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
						${isTouchDevice ? "pointer-events-none cursor-default" : ""}
						${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}
						${className}
					`}
					title={title}
					tabIndex={isTouchDevice ? -1 : tabIndex}
				/>

				{/* Increment Button (right side) */}
				<Button
					type="button"
					variant="outline"
					disabled={disabled || internalValue >= max}
					onClick={handleIncrement}
					aria-label="Erhöhen"
					tabIndex={-1}
					data-enabled={!(disabled || internalValue >= max)}
					className="h-9 w-7 p-0 rounded-r-md rounded-l-none border-input border-l-0 hover:bg-gray-50 flex items-center justify-center shadow-none"
				>
					<Plus size={10} strokeWidth={2} />
				</Button>
			</div>
		);
	},
);
